import React, {useEffect, useMemo, useCallback, Suspense} from 'react';
import {connect} from 'react-redux';
import {useParams, useHistory, useLocation} from 'react-router-dom';
import {Breadcrumb} from 'react-bootstrap';
import {Tune} from "@material-ui/icons";
import {Button as MButton} from "@material-ui/core";

// Actions
import {
	getAllProducts,
	getAllProductsFiltersOption,
	setFilters,
	resetFilter,
	toggleFilters,
	resetPages,
	setPage,
} from '../../store/actions/productAction';

// Components
import ProductList from '../../components/Products/ProductList';
import ProductFilters from '../../components/Products/ProductFilters/ProductFilters';
import InfiniteScrollPagination from '../../components/Products/Pagination/InfiniteScrollPagination';
import LazyLoader from "../../components/LazyLoader/LazyLoader";
import BreadcrumbSkeleton from "../../components/Breadcrumb/BreadcrumbSkeleton";
import CartPopUp from "../../containers/Cart/CartPopUp";
import ProductsTitle from "../../components/Products/ProductsTitle";
import ProductInfo from "../../components/Products/ProductInfo/ProductInfo";
import EmptyProducts from "../../components/Products/EmptyProducts/EmptyProducts";

// Hooks
import useBreadcrumbs from '../../hooks/useBreadcrumbs';
import {useDeviceType} from "../../helper/useDeviceType";
import {useCart} from "../../contexts/cart/use-cart";
import {useFavorites} from "../../contexts/favorites/use-favorites";
import {useURLSync} from '../../hooks/useURLSync';
import {useProductData} from '../../hooks/useProducts';
import {useProductFilters} from '../../hooks/useProductFilters';
import {useScrollManagement} from '../../hooks/useScrollManagement';
import {useEmptyState} from '../../hooks/useEmptyState';
import {usePageSEO} from '../../hooks/useSEO';

// Utils & Styles
import t from '../../i18n/translate';
import {
	MainContentArea,
	BreadcrumbWrapper,
	ContentSection,
	Title,
	FilterWrapper,
	FilterDiv
} from './Productspage.styles';
import './ProductsPage.css';
import './Breadcrumbs.css';

const ProductPage = ({
	                     products,
	                     filters,
	                     pagination,
	                     loading,
	                     error,
	                     filterOptions,
	                     showFilter,
	                     pageInfo,
	                     getAllProducts,
	                     getAllProductsFiltersOption,
	                     setFilters,
	                     resetPage,
	                     setPage,
	                     resetFilter,
	                     toggleFilters,
	                     userAgent,
                     }) => {
	const deviceType = useDeviceType(userAgent);
	const history = useHistory();
	const location = useLocation();
	const {catrefid, subcatrefid, q, pid, brandName, text, barcode, imageId, slug} = useParams();
	const targetRef = React.useRef(null);

	const search = window.location.pathname.split("/").includes("search");	// Get search parameters from URL query string
	const searchParams = new URLSearchParams(location.search);
	const barcodeParam = barcode || searchParams.get('barcode');
	const imageIdParam = imageId || searchParams.get('imageId') || searchParams.get('image_id');

	const mql = window.matchMedia("all and (min-width: 991px)");
	const modalClasses = `modal ${showFilter ? 'show animate' : ''}`;
	const filterBackgroundBlur = `bg_blur ${showFilter ? 'show' : ''}`;

	// Contexts
	const {fetchOrderItems} = useCart();
	const {fetchFavorites} = useFavorites();

	// Custom hooks
	const {isInitialSyncComplete} = useURLSync(filters, setFilters);

	useProductData({
		isInitialSyncComplete,
		filters,
		pagination,
		catrefid,
		subcatrefid,
		brandName,
		getAllProducts,
		getAllProductsFiltersOption,
		text,
		barcode: barcodeParam,
		imageId: imageIdParam,
		slug
	});

	const {applyFilters, clearFilters, initialFilters} = useProductFilters(
		filters,
		setFilters,
		resetPage,
		resetFilter,
		toggleFilters,
		showFilter
	);

	useScrollManagement(pagination.currentPage);
	const showEmpty = useEmptyState(loading, products.length);

	const {renderBreadcrumbs, isLoading: breadcrumbsLoading} = useBreadcrumbs({
		catrefid,
		subcatrefid,
		q,
		pid,
		brandName,
		slug
	});

	// SEO: Dynamic meta tags for partner/landing pages
	const pathSegments = location.pathname.split("/").filter(Boolean);
	const pageType = pathSegments[0] === "partner" ? "partner" : pathSegments[0] === "page" ? "landing" : null;

	usePageSEO(
		pageInfo?.title
			? {
				title: pageInfo.title,
				description: pageInfo.description,
				image: pageInfo.banner || pageInfo.logo,
				type: pageType,
			}
			: null
	);

	// Loading states
	const isInitialLoad = loading && pagination.currentPage === 1;
	const isLoadingMore = loading && pagination.currentPage > 1;

	// Initialize user data
	useEffect(() => {
		const user = localStorage.getItem("user");
		if (user) {
			fetchOrderItems();
			fetchFavorites({type: 0});
		}
	}, []);

	// Reset page on path change
	useEffect(() => {
		resetPage(1);
	}, [location.pathname, resetPage]);

	// Event handlers
	const handlePageChange = useCallback((page) => {
		setPage(page);
	}, [setPage]);

	const handleProductClick = useCallback((refId) => {
		history.push(`/product/${refId}`);
	}, [history]);

	// Computed values
	const hasMore = useMemo(
		() => pagination.currentPage < pagination.totalPages,
		[pagination.currentPage, pagination.totalPages]
	);

	// Render helpers
	const renderEmptyState = useCallback(
		() => <EmptyProducts handleClearFilters={clearFilters}/>,
		[clearFilters]
	);

	// Handle errors
	// if (error) {
	// 	history.push("/not-found");
	// 	return null;
	// }

	return (
		<React.Fragment>
			{deviceType.desktop && (
				<MainContentArea style={{paddingTop: mql.matches ? "100px" : "0px"}}>
					{breadcrumbsLoading || isInitialLoad ? (
						<BreadcrumbSkeleton/>
					) : (
						<BreadcrumbWrapper>
							<Breadcrumb>
								<MButton
									variant="contained"
									style={{
										minWidth: 54,
										marginInlineEnd: 10,
										padding: 6,
										backgroundColor: "#e9ecef",
									}}
									onClick={toggleFilters}
								>
									<Tune style={{fontSize: 26}}/>
								</MButton>
								{renderBreadcrumbs()}
							</Breadcrumb>
						</BreadcrumbWrapper>
					)}

					{!search && <ProductInfo
						loading={isInitialLoad}
						product={{
							banner: pageInfo?.banner,
							smallBanner: pageInfo?.smallBanner,
							title: pageInfo?.title,
							description: pageInfo?.description,
							logo: pageInfo?.logo,
							mainType: pageInfo?.mainType
						}}
					/>}

					<ContentSection
						className='contentSection'
						id="productBlock"
						style={{width: "100%"}}
					>
						<div ref={targetRef}>
							<Suspense fallback={<LazyLoader/>}>
								{isInitialLoad ? (
									<ProductsTitle/>
								) : (
									<Title itemProp="name">
										{t("products_in_category")}
									</Title>
								)}

								{!loading && products.length === 0 && showEmpty ? (
									<div style={{marginTop: "-50px"}}>
										{renderEmptyState()}
									</div>
								) : (
									<ProductList
										products={products}
										onProductClick={handleProductClick}
										loading={isInitialLoad}
										showSkeletonCount={20}
										isLoadMore={isLoadingMore}
									/>
								)}

								{pagination.totalPages > 1 && (
									<InfiniteScrollPagination
										currentPage={pagination.currentPage}
										totalPages={pagination.totalPages}
										totalItems={pagination.totalItems}
										itemsPerPage={pagination.itemsPerPage}
										onPageChange={handlePageChange}
										loading={isLoadingMore}
										hasMore={hasMore}
										threshold={1100}
									/>
								)}
							</Suspense>
						</div>
					</ContentSection>
				</MainContentArea>
			)}

			<CartPopUp deviceType={deviceType}/>

			<FilterWrapper>
				<FilterDiv>
					<div className={filterBackgroundBlur} onClick={toggleFilters}/>
					<div className={`${modalClasses} row`}>
						<ProductFilters
							loading={loading}
							filterOptions={filterOptions}
							applyFilter={applyFilters}
							showFilter={showFilter}
							toggleFilter={toggleFilters}
							resetFilters={clearFilters}
							initialFilters={initialFilters}
						/>
					</div>
				</FilterDiv>
			</FilterWrapper>
		</React.Fragment>
	);
};

ProductPage.getInitialProps = async (appContext) => {
	const {req} = appContext.ctx;
	const userAgent = req ? req.headers["user-agent"] : navigator.userAgent;
	return {userAgent};
};

const mapStateToProps = (state) => ({
	products: state.productSlice.products,
	filters: state.productSlice.filters,
	pagination: state.productSlice.pagination,
	loading: state.productSlice.loading,
	error: state.productSlice.error,
	filterOptions: state.productSlice.filterOptions,
	showFilter: state.productSlice.showFilter,
	pageInfo: state.productSlice.pageInfo,
});

const mapDispatchToProps = (dispatch) => ({
	resetPage: (page) => resetPages(dispatch, page),
	getAllProducts: (params) => getAllProducts(dispatch, params),
	getAllProductsFiltersOption: (params) => getAllProductsFiltersOption(dispatch, params),
	setFilters: (filters) => setFilters(dispatch, filters),
	setPage: (page) => setPage(dispatch, page),
	resetFilter: () => resetFilter(dispatch),
	toggleFilters: () => toggleFilters(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductPage);